/**
 * Phase 9 — 🏠 Home Activity Rail
 * --------------------------------
 * Renders the unified `/api/feed/activity` stream as a horizontal scroll
 * of compact event cards (new drops · collabs · spotlight wins · fan
 * boosts · big comments · playlist adds). Auto-refreshes every 60s. Each
 * card deep-links to the relevant track / room / profile.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const KIND_META = {
  new_drop:      { emoji: "🎵", label: "New Drop",        tint: "from-pink-500/15 to-purple-500/15 border-pink-500/30" },
  collab:        { emoji: "🤝", label: "Collab",          tint: "from-pink-500/15 to-rose-500/15 border-pink-400/40" },
  spotlight_win: { emoji: "🏆", label: "Spotlight",       tint: "from-amber-500/20 to-orange-500/15 border-amber-400/40" },
  fan_boost:     { emoji: "⚡", label: "Fan Boost",       tint: "from-amber-400/20 to-pink-500/15 border-amber-300/40" },
  comment:       { emoji: "💬", label: "Said",            tint: "from-purple-500/10 to-indigo-500/10 border-purple-500/30" },
  playlist_add:  { emoji: "📀", label: "Playlist Add",    tint: "from-indigo-500/15 to-violet-500/15 border-indigo-400/30" },
};

const ActivityCard = ({ item }) => {
  const meta = KIND_META[item.kind] || KIND_META.new_drop;
  const actor = item.actor || {};
  const cover = item.submission_id
    ? getCoverUrl({
        submission_id: item.submission_id,
        cover_url: item.cover_url,
        cover_updated_at: item.cover_updated_at,
      })
    : null;

  let title = actor.artist_name || "Someone";
  let subtitle = "";
  let to = "/home";

  if (item.kind === "new_drop" || item.kind === "collab") {
    subtitle = item.title || "released a new track";
    to = `/track/${item.submission_id}`;
  } else if (item.kind === "spotlight_win") {
    subtitle = `won Spotlight #${item.position || ""}`.trim();
    to = `/track/${item.submission_id}`;
  } else if (item.kind === "fan_boost") {
    subtitle = `${item.gift_emoji || "🎁"} ${item.gift_name || "Big gift"} · ${(item.coins || 0).toLocaleString()}c`;
    to = item.submission_id ? `/track/${item.submission_id}` : `/artist/${item.recipient_user_id || actor.user_id || ""}`;
  } else if (item.kind === "comment") {
    subtitle = `“${item.preview || ""}”`;
    to = `/track/${item.submission_id}`;
  } else if (item.kind === "playlist_add") {
    subtitle = `→ ${item.playlist_name || "playlist"}`;
    to = `/playlists/${item.playlist_id}`;
  }

  return (
    <Link
      to={to}
      className={`shrink-0 w-60 sm:w-64 rounded-xl border bg-gradient-to-br ${meta.tint} p-3 hover:scale-[1.02] transition-transform snap-start`}
      data-testid={`activity-card-${item.kind}-${item.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base" aria-hidden>{meta.emoji}</span>
        <span className="text-[10px] uppercase tracking-wider text-white/70 font-bold">{meta.label}</span>
      </div>
      <div className="flex gap-2 items-center">
        {cover ? (
          <img
            src={optimizeImage(cover, 'avatar')}
            alt=""
            className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
          />
        ) : (
          <img
            src={getProfilePhotoUrl(actor)}
            alt={actor.artist_name || ""}
            className="w-12 h-12 rounded-full object-cover border border-white/10 shrink-0"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
          />
        )}
        <div className="min-w-0">
          <p className="text-white text-sm font-semibold truncate">{title}</p>
          <p className="text-pink-100/70 text-xs line-clamp-2">{subtitle}</p>
        </div>
      </div>
    </Link>
  );
};

export const ActivityFeedRail = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/feed/activity?limit=20");
      setItems(r.data?.items || []);
    } catch (_e) {
      // Silent — degraded experience: rail just doesn't render.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);  // auto-refresh every 60s
    return () => clearInterval(id);
  }, []);

  if (loading && items.length === 0) {
    return (
      <div className="px-1 mb-6">
        <div className="h-24 rounded-xl bg-white/5 animate-pulse" />
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="mb-6" data-testid="activity-rail">
      <div className="flex items-end justify-between mb-2 px-1">
        <div>
          <h2 className="text-2xl font-black text-white">📰 Activity</h2>
          <p className="text-pink-200/60 text-xs">What just happened across RGM</p>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-thin">
        {items.map((it) => (
          <ActivityCard key={it.id} item={it} />
        ))}
      </div>
    </section>
  );
};

export default ActivityFeedRail;
